import { Lightbulb, Zap, Megaphone, Target, Sparkles, Gauge, Shield, TrendingUp } from "lucide-react";
import AvatarIndicator from "./AvatarIndicator";
import { CategoryScore, VisualIndicator } from "../types/diagnostics";

interface Props {
  overallPercentage: number;
  visualIndicator: VisualIndicator;
  categoryScores?: CategoryScore[];
  report: string[];
  recommendations: any[];
  services: string[];
}

export default function BusinessSummary({
  overallPercentage,
  visualIndicator,
  categoryScores = [],
  report,
  recommendations,
  services
}: Props) {
  // Prepare data for metric tiles dynamically
  const icons = [TrendingUp, Sparkles, Gauge, Shield];
  const colors = ["#4f46e5", "#2563eb", "#0891b2", "#475569"];

  const scoreData = categoryScores.map((cat, i) => ({
    name: cat.category,
    value: cat.percentage,
    color: colors[i % colors.length],
    icon: icons[i % icons.length] || Zap
  }));

  const handleBookingClick = () => {
    window.open('https://app.strivreservices.com/book/strivre-services', "_blank", "noopener,noreferrer");
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* 1. Hero Bento Grid (Overall Score & Metrics) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Main Visualizer Card */}
        <div className="lg:col-span-5 bg-white rounded-3xl border border-strivre-lightnavy shadow-sm p-8 flex flex-col items-center justify-center gap-6 relative overflow-hidden group">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 via-blue-500 to-cyan-500"></div>
          
          <div className="flex-shrink-0">
            <AvatarIndicator state={visualIndicator} />
          </div>

          <div className="flex flex-col items-center">
            <div className="bg-slate-50/50 backdrop-blur-sm px-10 py-6 rounded-[2.5rem] border border-slate-100 shadow-inner transition-all group-hover:bg-white group-hover:shadow-md text-center">
                <span className="text-7xl font-black text-slate-900 tracking-tighter leading-none block">
                  {overallPercentage}<span className="text-3xl text-indigo-600 font-bold ml-1">%</span>
                </span>
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-[0.2em] mt-3 block">
                  Business Maturity
                </span>
            </div>
          </div>
        </div>

        {/* Metric Tiles */}
        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {scoreData.map((s) => (
            <div key={s.name} className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 flex flex-col justify-between hover:border-indigo-200 transition-all group">
              <div className="flex justify-between items-start mb-4">
                <div className="p-3 rounded-2xl bg-slate-50 group-hover:bg-white transition-colors border border-transparent group-hover:border-slate-100" style={{ color: s.color }}>
                  {(() => { const Icon = s.icon; return <Icon size={24} />; })()}
                </div>
                <span className="text-4xl font-black tracking-tighter" style={{ color: s.color }}>{s.value}%</span>
              </div>
              <div>
                <h4 className="text-sm font-bold text-slate-800 uppercase tracking-tight mb-2">{s.name}</h4>
                <div className="w-full h-1.5 bg-strivre-softgrey rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${s.value}%`, backgroundColor: s.color }}></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 2. Key Opportunities */}
      <div className="bg-indigo-50/50 rounded-3xl border border-indigo-100 p-8 shadow-sm">
        <h4 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3">
          <div className="p-2 bg-strivre-burgundy rounded-lg text-white">
            <Lightbulb size={20} />
          </div>
          Key Business Opportunities
        </h4>
        {recommendations.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {recommendations.map((rec, i) => (
              <div key={i} className="flex items-start gap-4 p-5 bg-white rounded-2xl border border-indigo-50 shadow-sm">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-strivre-burgundy text-white flex items-center justify-center font-bold text-sm">
                  {i + 1}
                </span>
                <div className="text-left">
                  <p 
                    className="text-slate-700 text-sm font-normal leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: typeof rec === 'string' ? rec : rec.text }}
                  />
                  {rec.priority && (
                    <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-500 mt-1 block">Priority: {rec.priority}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-slate-500 text-center py-4">No major risks identified — keep building on your strengths!</p>
        )}
      </div>

      {/* 3. Mid CTA Section */}
      <div className="relative bg-gradient-to-br from-indigo-600 to-blue-800 rounded-[2.5rem] p-12 text-white text-center shadow-2xl overflow-hidden group">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 pointer-events-none"></div>
        <div className="relative z-10">
          <h3 className="text-3xl font-bold mb-4 tracking-tight text-white">Ready to Scale Your Business?</h3>
          <p className="text-indigo-100 text-lg leading-relaxed max-w-2xl mx-auto mb-8 font-normal">
            Turn these diagnostic insights into a roadmap for scalable growth.
            We specialize in optimizing the operational and digital structures of modern businesses.
          </p>
          <button onClick={handleBookingClick} className="px-10 py-4 bg-white text-indigo-700 font-bold rounded-full hover:shadow-xl hover:scale-105 transition-all active:scale-95">
            Schedule a Strategy Call
          </button>
        </div>
      </div>

      {/* 4. Analysis Summary Section */}
      <div className="bg-white rounded-3xl border border-strivre-lightnavy p-10 shadow-sm relative">
        <h3 className="text-2xl font-bold text-slate-900 mb-8 flex items-center gap-3">
          <Megaphone size={22} className="text-indigo-600" />
          Analysis Summary
        </h3>
        <div className="space-y-6 text-left border-l-4 border-indigo-100 pl-8 ml-2">
          {report.map((para, i) => (
            <p
              key={i}
              className="text-slate-600 leading-relaxed text-md font-normal"
              dangerouslySetInnerHTML={{ __html: para }}
            />
          ))}
        </div>
      </div>

      {/* 5. How We Can Help */}
      <div className="bg-emerald-50/50 rounded-3xl border border-emerald-100 p-8 shadow-sm">
        <h4 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
          <Zap size={24} className="text-emerald-600" />
          How We Can Help
        </h4>
        <p className="text-slate-700 mb-6 text-base leading-relaxed text-left">
          Based on your answers, here’s how we can support your business and digital strategy:
        </p>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {services.map((svc, i) => (
            <li key={i} className="flex items-center gap-3 p-4 bg-white rounded-xl border border-emerald-50">
              <Target size={20} className="text-emerald-600 flex-shrink-0" />
              <span className="text-slate-700 font-medium">{svc}</span>
            </li>
          ))}
        </ul>
        <p className="text-slate-500 mt-6 text-sm text-left italic">
          Partnering with us ensures your business remains modern, scalable, and ready for growth.
        </p>
      </div>
    </div>
  );
}
